import { useEffect, useRef, useState } from 'react';
import { fetch } from '../../utils/api-wrapper';
import { mergeSavedInvoiceJson } from '../../utils/invoice-save';
import { hasValidLineAmounts, invoiceTaxInputs } from './invoice-review';

// Keep draft taxes on the existing server calculator. Ignore old responses when
// the user changes another amount; preserve non-monetary edits made meanwhile.
export default function useInvoiceTaxes(json, setJson) {
  const signature = invoiceTaxInputs(json);
  const latest = useRef(json);
  latest.current = json;
  const [result, setResult] = useState({ signature: null, error: false });
  const [retry, setRetry] = useState(0);
  useEffect(() => {
    if (!latest.current || !hasValidLineAmounts(latest.current)) return undefined;
    if (result.signature === signature && !result.error) return undefined;
    let cancelled = false;
    const timer = setTimeout(() => {
      const sent = latest.current;
      const payload = { ...sent, ListItem: sent.ListItem.map(line => ({ ...line, Total: Number(line.Total) })) };
      fetch('/utils/compute_taxes', 'POST', payload, response => {
        if (cancelled) return;
        const calculated = response?.data?.json;
        if (!Array.isArray(calculated?.ListItem)) {
          setResult({ signature, error: true });
          return;
        }
        setJson(current => mergeSavedInvoiceJson(sent, current, calculated));
        setResult({ signature: invoiceTaxInputs(calculated), error: false });
      }, () => { if (!cancelled) setResult({ signature, error: true }); });
    }, 250);
    return () => { cancelled = true; clearTimeout(timer); };
    // Result changes must not retry a failed request in a loop; retry is explicit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature, retry, setJson]);
  return {
    calculating: Boolean(json && hasValidLineAmounts(json) && result.signature !== signature),
    taxError: result.signature === signature && result.error,
    retryTaxes: () => setRetry(value => value + 1),
  };
}
